import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { formatDateTime } from '../utils/dateFormat';

function truncateJson(details) {
  if (!details) return '--';
  try {
    const str = typeof details === 'string' ? details : JSON.stringify(details);
    return str.length > 80 ? str.slice(0, 80) + '...' : str;
  } catch {
    return '--';
  }
}

export default function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [sortBy, setSortBy] = useState('created_at');
  const [order, setOrder] = useState('desc');

  // Filter values from API response
  const [availableActions, setAvailableActions] = useState([]);
  const [availableEntityTypes, setAvailableEntityTypes] = useState([]);

  // Selected filters
  const [actionFilter, setActionFilter] = useState('');
  const [entityTypeFilter, setEntityTypeFilter] = useState('');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20, sortBy, order };
      if (actionFilter) params.action = actionFilter;
      if (entityTypeFilter) params.entityType = entityTypeFilter;
      const { data } = await api.get('/audit-logs', { params });
      setLogs(data.data);
      setPagination(data.pagination);
      if (data.filters) {
        if (data.filters.actions) setAvailableActions(data.filters.actions);
        if (data.filters.entityTypes) setAvailableEntityTypes(data.filters.entityTypes);
      }
    } catch {} finally {
      setLoading(false);
    }
  }, [page, sortBy, order, actionFilter, entityTypeFilter]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const handleSort = (col) => {
    if (sortBy === col) {
      setOrder(o => o === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setOrder('asc');
    }
    setPage(1);
  };

  const sortIcon = (col) => {
    if (sortBy !== col) return '';
    return order === 'asc' ? ' \u2191' : ' \u2193';
  };

  const colCount = 6;

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Audit Log</h1>
          <p className="text-secondary-500 text-sm mt-1">{pagination.total || 0} entries</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <select
          value={actionFilter}
          onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}
          className="px-4 py-2 border border-secondary-200 rounded-lg text-sm text-secondary-700 focus:ring-2 focus:ring-primary-500 outline-none"
        >
          <option value="">All actions</option>
          {availableActions.map((a) => (
            <option key={a} value={a}>{a.replace(/_/g, ' ')}</option>
          ))}
        </select>
        <select
          value={entityTypeFilter}
          onChange={(e) => { setEntityTypeFilter(e.target.value); setPage(1); }}
          className="px-4 py-2 border border-secondary-200 rounded-lg text-sm text-secondary-700 focus:ring-2 focus:ring-primary-500 outline-none"
        >
          <option value="">All entity types</option>
          {availableEntityTypes.map((t) => (
            <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary-50 text-secondary-600">
              <tr>
                <th className="px-5 py-3 text-left font-medium cursor-pointer select-none" onClick={() => handleSort('action')}>
                  Action{sortIcon('action')}
                </th>
                <th className="px-5 py-3 text-left font-medium cursor-pointer select-none" onClick={() => handleSort('entity_type')}>
                  Entity Type{sortIcon('entity_type')}
                </th>
                <th className="px-5 py-3 text-left font-medium">Entity ID</th>
                <th className="px-5 py-3 text-left font-medium">User</th>
                <th className="px-5 py-3 text-left font-medium">Details</th>
                <th className="px-5 py-3 text-left font-medium cursor-pointer select-none" onClick={() => handleSort('created_at')}>
                  Created{sortIcon('created_at')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-secondary-100">
              {loading && (
                <tr><td colSpan={colCount} className="px-5 py-8 text-center text-secondary-400">Loading...</td></tr>
              )}
              {!loading && logs.length === 0 && (
                <tr><td colSpan={colCount} className="px-5 py-8 text-center text-secondary-400">No audit entries found</td></tr>
              )}
              {!loading && logs.map((log) => (
                <tr key={log.id} className="hover:bg-secondary-50 transition">
                  <td className="px-5 py-3 text-secondary-700 whitespace-nowrap font-medium">
                    {log.action?.replace(/_/g, ' ')}
                  </td>
                  <td className="px-5 py-3 text-secondary-600 whitespace-nowrap">
                    {log.entity_type?.replace(/_/g, ' ')}
                  </td>
                  <td className="px-5 py-3 text-secondary-500 text-xs font-mono">{log.entity_id || '--'}</td>
                  <td className="px-5 py-3 text-secondary-600">{log.user_email || log.user_id || '--'}</td>
                  <td className="px-5 py-3 text-secondary-500 text-xs max-w-xs truncate" title={typeof log.details === 'object' ? JSON.stringify(log.details) : log.details}>
                    {truncateJson(log.details)}
                  </td>
                  <td className="px-5 py-3 text-secondary-500 text-xs whitespace-nowrap">{formatDateTime(log.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-5 py-3 border-t border-secondary-100 flex items-center justify-between">
            <p className="text-sm text-secondary-500">Page {pagination.page} of {pagination.totalPages}</p>
            <div className="flex gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                className="px-3 py-1 text-sm rounded-lg border border-secondary-200 hover:bg-secondary-50 disabled:opacity-40">Prev</button>
              <button onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))} disabled={page >= pagination.totalPages}
                className="px-3 py-1 text-sm rounded-lg border border-secondary-200 hover:bg-secondary-50 disabled:opacity-40">Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
